import { NavLink, Outlet, Navigate, Link } from "react-router-dom";
import {
    LayoutDashboard, Users, Film, FileText, HelpCircle, Tag, Download,
    MessageSquareQuote, Inbox, ScrollText, Settings, UserCircle, LogOut, ArrowLeft, ShoppingBag, BarChart3,
} from "lucide-react";
import { useAuth, isStaff, isAdmin } from "../context/AuthContext";
import { Logo } from "../components/Logo";

const MENU = [
    { to: "/admin", label: "Tableau de bord", icon: LayoutDashboard, end: true, staff: true },
    { to: "/admin/audience", label: "Audience & Conversions", icon: BarChart3, staff: true },
    { to: "/admin/utilisateurs", label: "Utilisateurs", icon: Users, admin: true },
    { to: "/admin/medias", label: "Médias & Vidéos", icon: Film, staff: true },
    { to: "/admin/contenu", label: "Contenu du site", icon: FileText, staff: true },
    { to: "/admin/faq", label: "FAQ", icon: HelpCircle, staff: true },
    { to: "/admin/tarifs", label: "Tarifs & promotions", icon: Tag, staff: true },
    { to: "/admin/boutique", label: "Boutique", icon: ShoppingBag, staff: true },
    { to: "/admin/telechargements", label: "Téléchargements", icon: Download, admin: true },
    { to: "/admin/temoignages", label: "Témoignages", icon: MessageSquareQuote, staff: true },
    { to: "/admin/messages", label: "Messages", icon: Inbox, staff: true },
    { to: "/admin/audit", label: "Journal d'audit", icon: ScrollText, admin: true },
    { to: "/admin/parametres", label: "Paramètres", icon: Settings, admin: true },
    { to: "/admin/compte", label: "Mon compte", icon: UserCircle, staff: true },
];

const ROLE_BADGES = {
    super_admin: "Super-admin",
    admin: "Admin",
    editor: "Éditeur",
    moderator: "Modérateur",
    user: "Utilisateur",
};

export default function AdminLayout() {
    const { user, logout } = useAuth();

    if (user === null) return <div className="min-h-screen bg-ivory" aria-label="Chargement" />;
    if (!user || !isStaff(user)) return <Navigate to="/connexion" replace />;

    const items = MENU.filter((m) => (m.admin ? isAdmin(user) : true));

    return (
        <div className="min-h-screen bg-cream-subtle flex" data-testid="admin-layout">
            <aside className="w-64 shrink-0 bg-white border-r border-border min-h-screen sticky top-0 hidden md:flex flex-col">
                <div className="p-5 border-b border-border">
                    <Logo dark={false} />
                    <p className="text-[11px] font-mono uppercase tracking-widest text-gold-dark mt-2">Administration</p>
                </div>
                <nav className="flex-1 p-3 space-y-1 overflow-y-auto" aria-label="Menu administration">
                    {items.map((m) => (
                        <NavLink
                            key={m.to}
                            to={m.to}
                            end={m.end}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                                    isActive ? "bg-forest/10 text-forest font-semibold" : "text-ink-soft hover:bg-pearl hover:text-ink"
                                }`
                            }
                            data-testid={`admin-nav-${m.to.split("/").pop() || "dashboard"}`}
                        >
                            <m.icon size={17} /> {m.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="p-4 border-t border-border space-y-3">
                    <div className="text-xs">
                        <p className="text-ink font-medium truncate">{user.name}</p>
                        <p className="text-ink-soft truncate">{user.email}</p>
                        <span className="inline-block mt-1.5 text-[10px] font-mono uppercase tracking-wider bg-gold/15 text-gold-dark px-2 py-0.5 rounded-full">
                            {ROLE_BADGES[user.role]}
                        </span>
                    </div>
                    <div className="flex gap-2">
                        <Link to="/" className="text-xs text-ink-soft hover:text-forest flex items-center gap-1" data-testid="admin-back-to-site">
                            <ArrowLeft size={13} /> Site
                        </Link>
                        <button onClick={logout} className="text-xs text-ink-soft hover:text-red-600 flex items-center gap-1 ml-auto" data-testid="admin-logout-button">
                            <LogOut size={13} /> Déconnexion
                        </button>
                    </div>
                </div>
            </aside>
            <main className="flex-1 min-w-0 p-4 sm:p-8">
                <div className="md:hidden mb-6 flex items-center justify-between">
                    <Logo dark={false} />
                    <button onClick={logout} className="text-sm text-pineneedle" data-testid="admin-logout-mobile">Déconnexion</button>
                </div>
                <div className="md:hidden mb-6 flex gap-2 overflow-x-auto pb-2">
                    {items.map((m) => (
                        <NavLink key={m.to} to={m.to} end={m.end} className={({ isActive }) => `whitespace-nowrap px-3 py-1.5 rounded-full text-xs border ${isActive ? "bg-forest text-cream border-forest" : "border-border text-pineneedle"}`}>
                            {m.label}
                        </NavLink>
                    ))}
                </div>
                <Outlet />
            </main>
        </div>
    );
}

export const PageHeader = ({ title, subtitle, children }) => (
    <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
            <h1 className="font-heading font-extrabold text-forest text-2xl sm:text-3xl tracking-tight" data-testid="admin-page-title">{title}</h1>
            {subtitle && <p className="text-pineneedle mt-1 text-sm">{subtitle}</p>}
        </div>
        {children}
    </div>
);
